import { useState, useEffect, useCallback } from 'react';
import { useReaderAuth } from '../context/ReaderAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook to manage saved/bookmarked articles for the current reader.
 */
export const useSavedArticles = () => {
  const { token, isAuthenticated } = useReaderAuth();
  const [savedArticles, setSavedArticles] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const isLoggedIn = typeof isAuthenticated === 'function' ? isAuthenticated() : false;

  const fetchSaved = useCallback(async () => {
    if (!isLoggedIn || !token) { setSavedArticles([]); setSavedIds(new Set()); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/reader/articles/saved`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) {
        const data = await r.json();
        setSavedArticles(data);
        setSavedIds(new Set(data.map(a => a.article_id)));
      }
    } catch (e) { console.error('fetchSaved error', e); }
    finally { setLoading(false); }
  }, [isLoggedIn, token]);

  useEffect(() => { fetchSaved(); }, [fetchSaved]);

  const saveArticle = async (article) => {
    if (!isLoggedIn || !token) return;
    await fetch(`${API_URL}/api/reader/articles/save`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        article_id: article.id,
        article_slug: article.slug,
        article_title: article.title,
        article_excerpt: article.excerpt,
        article_image: article.image || '',
        article_category: article.category || ''
      })
    });
    await fetchSaved();
  };

  const unsaveArticle = async (articleId) => {
    if (!isLoggedIn || !token) return;
    await fetch(`${API_URL}/api/reader/articles/saved/${articleId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    setSavedIds(prev => { const n = new Set(prev); n.delete(articleId); return n; });
    setSavedArticles(prev => prev.filter(a => a.article_id !== articleId));
  };

  const toggleSave = async (article) => {
    if (savedIds.has(article.id)) {
      await unsaveArticle(article.id);
    } else {
      await saveArticle(article);
    }
  };

  return { savedArticles, savedIds, loading, toggleSave, unsaveArticle, refetch: fetchSaved };
};

export default useSavedArticles;
